<template>
  <template
    v-if="
      authStore.isAuthenticated &&
      (notifStore.hasChatAlerts || (authStore.hasRole('admin') && notifStore.hasAlerts))
    "
  >
    <div class="relative">
      <!-- Bell button with alert count badge -->
      <button
        type="button"
        title="View notifications"
        class="relative p-2 rounded-lg text-amber-400 hover:text-amber-300 hover:bg-charcoal-800/10 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400/50"
        @click="handleBellClick"
      >
        <Bell :size="20" :stroke-width="2" />
        <span
          class="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center px-1 leading-none"
        >
          {{ notifStore.totalAlerts }}
        </span>
      </button>

      <!-- Unified notifications popover -->
      <Transition name="fade">
        <div
          v-if="popoverOpen"
          class="absolute right-0 top-12 w-80 bg-charcoal-800 border border-charcoal-600 rounded-xl shadow-2xl z-50 overflow-hidden"
        >
          <!-- Header -->
          <div class="px-4 py-3 border-b border-charcoal-700 flex items-center justify-between">
            <span class="text-sm font-bold text-charcoal-100">Notifications</span>
            <button
              class="text-xs text-charcoal-400 hover:text-charcoal-300 transition-colors"
              @click="popoverOpen = false"
            >
              Close
            </button>
          </div>

          <!-- Chat section -->
          <template v-if="notifStore.hasChatAlerts">
            <div
              class="px-4 py-2 bg-charcoal-900 flex items-center gap-2 text-[11px] font-semibold text-charcoal-400 uppercase tracking-wide"
            >
              <MessageSquare :size="12" class="text-green-500" /> Proposal Chat
            </div>
            <ul class="max-h-48 overflow-y-auto divide-y divide-charcoal-700">
              <li v-for="item in chatProposals" :key="item.proposalId">
                <button
                  type="button"
                  class="w-full text-left px-4 py-3 hover:bg-charcoal-900 cursor-pointer transition-colors"
                  @click="handleChatItemClick(item)"
                >
                  <div class="flex items-start justify-between gap-2">
                    <span class="text-xs font-semibold text-charcoal-200 truncate flex-1">
                      {{ item.reportId }}
                    </span>
                    <span
                      v-if="isUnread(item)"
                      class="shrink-0 w-2 h-2 rounded-full bg-green-500 mt-1"
                      title="Unread messages"
                    />
                  </div>
                  <p class="text-[11px] text-charcoal-400 mt-0.5">
                    Last message {{ formatRelative(item.latestMessageAt) }}
                  </p>
                </button>
              </li>
            </ul>
          </template>

          <!-- Stale reports section (admin only) -->
          <template v-if="authStore.hasRole('admin') && notifStore.staleCount > 0">
            <div
              class="px-4 py-2 bg-charcoal-900 flex items-center gap-2 text-[11px] font-semibold text-charcoal-400 uppercase tracking-wide"
            >
              <AlertTriangle :size="12" class="text-amber-500" /> Stale Reports
            </div>
            <button
              class="w-full text-left px-4 py-3 hover:bg-charcoal-900 transition-colors"
              @click="openStaleReports"
            >
              <p class="text-xs font-semibold text-charcoal-200">
                {{ notifStore.staleCount }} stale
                {{ notifStore.staleCount === 1 ? 'report' : 'reports' }} to review
              </p>
              <p class="text-[11px] text-amber-500 mt-0.5">Tap to view and resolve →</p>
            </button>
          </template>
        </div>
      </Transition>
    </div>

    <!-- Stale-reports drawer (admin only) -->
    <ProposalPipelineDrawer
      v-if="authStore.hasRole('admin')"
      :open="notifStore.drawerOpen"
      status="stale_reports"
      :proposals="[]"
      :reports="[]"
      :stale-surveys="notifStore.staleSurveys"
      :not-started-surveys="[]"
      :valid-surveys="notifStore.validSurveys"
      :loading="false"
      :count="notifStore.staleCount"
      :error="notifStore.drawerError"
      @close="notifStore.closeDrawer()"
      @delete-stale-all="handleDeleteStaleAll"
      @delete-stale-survey="handleDeleteStaleSurvey"
      @relink-stale-survey="handleRelinkStaleSurvey"
      @convert-adhoc-stale="handleConvertAdhocStale"
    />
  </template>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Bell, MessageSquare, AlertTriangle } from 'lucide-vue-next'
import { useAuthStore } from '@/stores/auth.store'
import { useNotificationsStore } from '@/stores/notifications.store'
import ProposalPipelineDrawer from '@/components/ProposalPipelineDrawer.vue'

const authStore = useAuthStore()
const notifStore = useNotificationsStore()
const router = useRouter()

// ── Chat popover state ────────────────────────────────────────────────────────

interface ChatProposalItem {
  proposalId: string
  reportId: string
  surveyId: string | null
  latestMessageAt: string
  totalMessages: number
}

const chatProposals = ref<ChatProposalItem[]>([])
const popoverOpen = ref(false)

const LAST_SEEN_KEY = 'proposalChat.lastSeen'

function getLastSeenMap(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(LAST_SEEN_KEY) ?? '{}') as Record<string, string>
  } catch {
    return {}
  }
}

function isUnread(item: ChatProposalItem): boolean {
  const seen = getLastSeenMap()
  const lastSeen = seen[item.proposalId]
  if (!lastSeen) return true
  return new Date(item.latestMessageAt) > new Date(lastSeen)
}

async function fetchChatUnread() {
  if (!authStore.isAuthenticated) return
  const tokenResult = await authStore.getAccessToken()
  if (!tokenResult.ok) return
  try {
    const bffUrl = import.meta.env.VITE_BFF_URL || '/api'
    const res = await fetch(`${bffUrl}/proposals/chat/unread`, {
      headers: { Authorization: `Bearer ${tokenResult.value}` },
    })
    if (!res.ok) return
    const data = (await res.json()) as { proposals: ChatProposalItem[] }
    chatProposals.value = data.proposals
    // Count proposals with unread messages
    const unreadCount = data.proposals.filter(p => isUnread(p)).length
    notifStore.setChatMessageCount(unreadCount)
  } catch {
    // Silent fail — bell just won't show chat badge
  }
}

function handleBellClick() {
  popoverOpen.value = !popoverOpen.value
}

function openStaleReports() {
  popoverOpen.value = false
  notifStore.openStaleReportsDrawer()
}

async function handleChatItemClick(item: ChatProposalItem) {
  popoverOpen.value = false
  // Mark this proposal as seen
  const map = getLastSeenMap()
  map[item.proposalId] = item.latestMessageAt
  localStorage.setItem(LAST_SEEN_KEY, JSON.stringify(map))
  const newUnreadCount = chatProposals.value.filter(p => isUnread(p)).length
  notifStore.setChatMessageCount(newUnreadCount)

  // Navigate to the appendices page for this proposal.
  // Survey-linked reports use /surveys/:surveyId/appendices;
  // adhoc/blank reports fall back to /proposals/:reportId/appendices.
  const destination = item.surveyId
    ? `/surveys/${item.surveyId}/appendices?openChat=true`
    : `/proposals/${item.reportId}/appendices?openChat=true`
  await router.push(destination)
}

function formatRelative(isoString: string): string {
  try {
    const diff = Date.now() - new Date(isoString).getTime()
    const mins = Math.floor(diff / 60_000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins}m ago`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `${hrs}h ago`
    return `${Math.floor(hrs / 24)}d ago`
  } catch {
    return ''
  }
}

onMounted(() => {
  fetchChatUnread()
})

// Re-fetch when auth becomes ready (onMounted fires before auth resolves)
watch(
  () => authStore.isAuthenticated,
  authed => {
    if (authed) fetchChatUnread()
  }
)

// Poll every 30s to pick up new messages
let chatPollInterval: ReturnType<typeof setInterval> | null = null
onMounted(() => {
  chatPollInterval = setInterval(() => {
    fetchChatUnread()
  }, 30_000)
})
onUnmounted(() => {
  if (chatPollInterval) clearInterval(chatPollInterval)
})

function getApiUrl(): string {
  return import.meta.env.VITE_BFF_URL || '/api'
}

async function getAuthToken(): Promise<string | null> {
  const result = await authStore.getAccessToken()
  return result.ok ? result.value : null
}

async function handleDeleteStaleAll() {
  const token = await getAuthToken()
  if (!token) return
  notifStore.setDrawerError(null)
  try {
    const res = await fetch(`${getApiUrl()}/proposals/stale-reports`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ deleteStale: true, validSurveyIds: notifStore.validSurveyIds }),
    })
    if (res.ok) {
      notifStore.clearAllStale()
    } else {
      const data = await res.json().catch(() => ({}))
      notifStore.setDrawerError(
        (data as { error?: string }).error ?? 'Failed to delete. Please try again.'
      )
    }
  } catch {
    notifStore.setDrawerError('Failed to delete. Please try again.')
  }
}

async function handleDeleteStaleSurvey(surveyId: string) {
  const token = await getAuthToken()
  if (!token) return
  notifStore.setDrawerError(null)
  try {
    const res = await fetch(`${getApiUrl()}/proposals/stale-reports`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        deleteStale: true,
        surveyId,
        validSurveyIds: notifStore.validSurveyIds,
      }),
    })
    if (res.ok) {
      const data = (await res.json()) as { deleted: number }
      notifStore.removeStale(surveyId, data.deleted)
    } else {
      const data = await res.json().catch(() => ({}))
      notifStore.setDrawerError(
        (data as { error?: string }).error ?? 'Failed to delete. Please try again.'
      )
    }
  } catch {
    notifStore.setDrawerError('Failed to delete. Please try again.')
  }
}

async function handleRelinkStaleSurvey(payload: { oldSurveyId: string; newSurveyId: string }) {
  const token = await getAuthToken()
  if (!token) return
  notifStore.setDrawerError(null)
  try {
    const res = await fetch(`${getApiUrl()}/proposals/stale-reports/relink`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        oldSurveyId: payload.oldSurveyId,
        newSurveyId: payload.newSurveyId,
        validSurveyIds: notifStore.validSurveyIds,
      }),
    })
    if (res.ok) {
      notifStore.removeStale(payload.oldSurveyId)
    } else {
      const data = await res.json().catch(() => ({}))
      notifStore.setDrawerError(
        (data as { error?: string }).error ?? 'Failed to re-link. Please try again.'
      )
    }
  } catch {
    notifStore.setDrawerError('Failed to re-link. Please try again.')
  }
}

async function handleConvertAdhocStale(surveyId: string) {
  const token = await getAuthToken()
  if (!token) return
  notifStore.setDrawerError(null)
  try {
    const res = await fetch(`${getApiUrl()}/proposals/stale-reports/convert-adhoc`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ surveyId, validSurveyIds: notifStore.validSurveyIds }),
    })
    if (res.ok) {
      notifStore.removeStale(surveyId)
    } else {
      const data = await res.json().catch(() => ({}))
      notifStore.setDrawerError(
        (data as { error?: string }).error ?? 'Failed to convert. Please try again.'
      )
    }
  } catch {
    notifStore.setDrawerError('Failed to convert. Please try again.')
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
